// Admin "Login as Merchant" impersonation session.
//
// This must be TRUE only when the admin actually went through the real
// flow: fetched the merchant list from GET /fetchMerchantForMode in
// LoginAsMerchant.js, picked one, and pressed "Login". It must NOT be
// derived just from "admin_token + customer_id both happen to be in
// localStorage" — those two keys are set independently by unrelated flows
// (AdminLogin.js only ever sets admin_token and never touches customer_id;
// Login.js/Register.js/PromoLogin.js only ever set customer_id and never
// touch admin_token; admin Logout only clears admin_token, not a leftover
// customer_id). On a shared/test browser those two keys can end up present
// together without any impersonation ever happening, so an explicit flag —
// set in exactly one place, handleLoginAsMerchant — is the only reliable
// signal.
const ADMIN_MERCHANT_MODE_KEY = "admin_merchant_mode_active";

export const isAdminMerchantMode = () =>
  !!localStorage.getItem(ADMIN_MERCHANT_MODE_KEY) &&
  !!localStorage.getItem("admin_token") &&
  !!localStorage.getItem("customer_id");

// The merchant customer_id the admin selected in LoginAsMerchant — the only
// identifier every merchant-scoped API call should use while impersonating.
export const getActiveMerchantCustomerId = () => localStorage.getItem("customer_id");

// Called exclusively from LoginAsMerchant.js's handleLoginAsMerchant, right
// after a merchant picked from the fetchMerchantForMode list is confirmed.
export const enterAdminMerchantMode = () => {
  localStorage.setItem(ADMIN_MERCHANT_MODE_KEY, "true");
};

// Leaves Merchant Mode. Keeps admin_token so the admin stays signed into
// the admin panel; only clears the merchant session pieces.
export const exitAdminMerchantMode = () => {
  localStorage.removeItem(ADMIN_MERCHANT_MODE_KEY);
  localStorage.removeItem("customer_id");
  localStorage.removeItem("phone");
};

// Defensive cleanup for entry points that are NOT the admin impersonation
// flow (a real merchant login, or a fresh admin login) — clears a stale
// flag that could otherwise have been left behind by an admin session that
// ended without pressing "Back to Admin" (e.g. plain admin Logout, which
// only removes admin_token and leaves customer_id/this flag behind).
// Also clears a stale Promo Login flag for the same reason (see below).
export const clearStaleAdminMerchantModeFlag = () => {
  localStorage.removeItem(ADMIN_MERCHANT_MODE_KEY);
  localStorage.removeItem(PROMO_LOGIN_MODE_KEY);
};

// Promo device login (PromoLogin.js) session.
//
// PromoLogin links a device via customerId + phone from a URL and never
// establishes a Firebase auth session. Every store-scoped screen normally
// resolves its active store/merchant data through a Firebase-uid-based
// lookup (GET /merchantDetails/:user_id, then POST /getMerchantStoreDetails
// with the numeric account_user.id it returns) — neither of those works for
// a promo session, since there's no Firebase uid and /promoLogin's response
// doesn't carry the numeric id either (see getCustomerByPromo's SELECT in
// Backend/database/mysqlDB.js — it only returns customer_id, mobile, name).
//
// This explicit flag — set in exactly one place, PromoLogin's LoginDevice —
// lets those screens skip the Firebase-uid resolution entirely and trust
// the customer_id already written to localStorage directly, the same way
// isAdminMerchantMode() does for admin impersonation.
const PROMO_LOGIN_MODE_KEY = "promo_login_mode_active";

export const isPromoLoginMode = () =>
  !!localStorage.getItem(PROMO_LOGIN_MODE_KEY) &&
  !!localStorage.getItem("customer_id");

// Called exclusively from PromoLogin.js's LoginDevice, right after a
// successful /promoLogin response.
export const enterPromoLoginMode = () => {
  localStorage.setItem(PROMO_LOGIN_MODE_KEY, "true");
};

// Leaves Promo Login mode — mirrors exitAdminMerchantMode. Not currently
// called anywhere (promo devices don't have a "log out" affordance yet),
// but kept alongside enterPromoLoginMode for symmetry and future use.
export const exitPromoLoginMode = () => {
  localStorage.removeItem(PROMO_LOGIN_MODE_KEY);
  localStorage.removeItem("customer_id");
  localStorage.removeItem("phone");
  localStorage.removeItem("merchant_name");
};
